import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, type JiraIssue } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { PageHeader } from '../components/PageHeader.js';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function statusColor(status: string): string {
  if (status === 'In Progress') return 'var(--accent-yellow)';
  if (status === 'Done') return 'var(--accent-green)';
  if (status === 'Blocked') return 'var(--accent-red)';
  return 'var(--accent-blue)';
}

function statusBg(status: string): string {
  if (status === 'In Progress') return 'var(--accent-yellow)22';
  if (status === 'Done') return 'var(--accent-green)22';
  if (status === 'Blocked') return 'var(--accent-red)22';
  return 'var(--accent-blue)22';
}

function statusBorder(status: string): string {
  if (status === 'In Progress') return 'var(--accent-yellow)44';
  if (status === 'Done') return 'var(--accent-green)44';
  if (status === 'Blocked') return 'var(--accent-red)44';
  return 'var(--accent-blue)44';
}

function parseGoLiveDate(raw: string): Date | null {
  // Strip non-numeric prefixes like "T-" before the date
  const cleaned = raw.replace(/^[^0-9]+/, '');
  if (cleaned.includes('/')) {
    const parts = cleaned.split('/');
    const mm = Number(parts[0]);
    const dd = Number(parts[1]);
    const yyyy = Number(parts[2]);
    if (!mm || !dd || !yyyy) return null;
    const d = new Date(yyyy, mm - 1, dd, 12, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }
  if (cleaned.length >= 10) {
    const d = new Date(cleaned.slice(0, 10) + 'T12:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function issuesByDay(issues: JiraIssue[], year: number, month: number): Map<number, JiraIssue[]> {
  const map = new Map<number, JiraIssue[]>();
  for (const issue of issues) {
    if (!issue.goLiveDate) continue;
    const d = parseGoLiveDate(issue.goLiveDate);
    if (!d) continue;
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      const arr = map.get(day) ?? [];
      arr.push(issue);
      map.set(day, arr);
    }
  }
  return map;
}

function issuesByMonth(issues: JiraIssue[]): Map<string, JiraIssue[]> {
  const map = new Map<string, JiraIssue[]>();
  for (const issue of issues) {
    if (!issue.goLiveDate) continue;
    const d = parseGoLiveDate(issue.goLiveDate);
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const arr = map.get(key) ?? [];
    arr.push(issue);
    map.set(key, arr);
  }
  return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
}

function MiniMonth({ year, month, issues, onClick }: {
  year: number; month: number; issues: JiraIssue[]; onClick: () => void;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const byDay = issuesByDay(issues, year, month);
  const today = new Date();

  return (
    <div
      className="rounded-xl p-3 cursor-pointer"
      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
      onClick={onClick}
    >
      <div className="text-xs font-semibold mb-2 text-center" style={{ color: 'var(--text-secondary)' }}>
        {(MONTH_NAMES[month] ?? '').slice(0, 3)} {year}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[8px]" style={{ color: 'var(--text-dimmed)' }}>{d[0]}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`e-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
          const hasEvent = byDay.has(day);
          return (
            <div
              key={day}
              className="text-center text-[9px] rounded aspect-square flex items-center justify-center"
              style={{
                color: isToday ? 'var(--accent-yellow)' : hasEvent ? 'var(--accent-blue)' : 'var(--text-dimmed)',
                background: isToday ? 'var(--accent-yellow)11' : hasEvent ? 'var(--accent-blue)11' : 'transparent',
                fontWeight: hasEvent || isToday ? 600 : 400,
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CalendarPage() {
  const navigate = useNavigate();
  usePageTitle('Calendar');
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view, setView] = useState<'month' | 'year'>('month');
  const [agendaOpen, setAgendaOpen] = useState(true);
  const agendaRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['jira', ''],
    queryFn: () => api.listJiraIssues(),
    retry: false,
  });

  const issues: JiraIssue[] = (data?.issues ?? []).filter((i) => i.goLiveDate && parseGoLiveDate(i.goLiveDate) !== null);
  const byDay = issuesByDay(issues, year, month);
  const byMonth = issuesByMonth(issues);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const isNotConfigured = error instanceof Error && error.message.toLowerCase().includes('not configured');

  function prevMonth() {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else setMonth((m) => m - 1);
  }

  function nextMonth() {
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else setMonth((m) => m + 1);
  }

  function scrollAgendaToMonth(y: number, m: number) {
    const key = `${y}-${String(m + 1).padStart(2, '0')}`;
    agendaRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Go-Live Dates"
        title="Calendar"
        actions={
          <button
            type="button"
            onClick={() => setAgendaOpen((v) => !v)}
            className="rounded-xl px-3 py-1.5 text-sm font-semibold"
            style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', color: 'var(--accent-blue)', border: '1px solid var(--glass-border)' }}
          >
            {agendaOpen ? '◀ Agenda' : '▶ Agenda'}
          </button>
        }
      />

      {(isNotConfigured || (error && !isNotConfigured)) && (
        <div className="px-6 pt-4 text-sm" style={{ color: isNotConfigured ? 'var(--text-dimmed)' : 'var(--accent-red)' }}>
          {isNotConfigured
            ? 'Configure Jira in Settings to load calendar data.'
            : error instanceof Error ? error.message : 'Failed to load'}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden gap-4 p-6">
        {/* Calendar panel */}
        <div className="flex-1 min-w-0">
          <div
            className="rounded-2xl p-5 h-full flex flex-col"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
          >
            {/* Controls */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                {view === 'month' && (
                  <button type="button" onClick={prevMonth} className="rounded-lg px-2 py-1 text-sm" style={{ background: 'var(--bg-tertiary)', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)33' }}>◀</button>
                )}
                <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  {view === 'month' ? `${MONTH_NAMES[month]} ${year}` : String(year)}
                </span>
                {view === 'month' && (
                  <button type="button" onClick={nextMonth} className="rounded-lg px-2 py-1 text-sm" style={{ background: 'var(--bg-tertiary)', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)33' }}>▶</button>
                )}
              </div>
              <div className="flex gap-1">
                {(['month', 'year'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    className="rounded-lg px-3 py-1 text-sm font-medium capitalize"
                    style={view === v
                      ? { background: 'linear-gradient(135deg, #0dcfb8, #06b6d4)', color: '#fff' }
                      : { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Month view */}
            {view === 'month' && (
              <>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {DAY_NAMES.map((d) => (
                    <div key={d} className="text-center text-xs font-semibold uppercase tracking-wide py-1" style={{ color: 'var(--text-dimmed)' }}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1" style={{ gridAutoRows: '1fr', flex: 1, minHeight: 0 }}>
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <div key={`e-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                    const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                    const dayIssues = byDay.get(day) ?? [];
                    const hasEvent = dayIssues.length > 0;
                    const VISIBLE = 3;
                    const overflow = dayIssues.length > VISIBLE ? dayIssues.length - VISIBLE : 0;
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => { if (hasEvent) { setAgendaOpen(true); scrollAgendaToMonth(year, month); } }}
                        className="rounded-lg flex flex-col gap-0.5 p-1.5 text-left transition-all w-full overflow-hidden"
                        style={{
                          background: hasEvent
                            ? 'rgba(13,207,184,0.12)'
                            : isToday
                            ? 'rgba(255,209,102,0.08)'
                            : 'transparent',
                          border: hasEvent
                            ? '1px solid rgba(13,207,184,0.35)'
                            : isToday
                            ? '1px solid rgba(255,209,102,0.35)'
                            : '1px solid transparent',
                          cursor: hasEvent ? 'pointer' : 'default',
                        }}
                      >
                        <span
                          className="text-xs font-semibold leading-none self-end shrink-0"
                          style={{
                            color: isToday ? 'var(--accent-yellow)' : hasEvent ? 'var(--text-primary)' : 'var(--text-dimmed)',
                          }}
                        >
                          {day}
                        </span>
                        {hasEvent && (
                          <div className="flex flex-col gap-0.5 w-full min-h-0 overflow-hidden">
                            {dayIssues.slice(0, VISIBLE).map((issue) => (
                              <div
                                key={issue.key}
                                className="rounded px-1 py-0.5 w-full min-w-0"
                                style={{ background: 'rgba(13,207,184,0.18)', border: '1px solid rgba(13,207,184,0.25)' }}
                              >
                                <div className="flex items-center gap-1 min-w-0">
                                  <span className="font-mono text-[9px] shrink-0" style={{ color: 'var(--accent-blue)' }}>{issue.key}</span>
                                  {issue.assignee && (
                                    <span
                                      className="ml-auto shrink-0 h-3.5 w-3.5 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                                      style={{ background: 'linear-gradient(135deg,#0dcfb8,#06b6d4)', flexShrink: 0 }}
                                      title={issue.assignee}
                                    >
                                      {issue.assignee[0]?.toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] font-medium leading-tight truncate" style={{ color: 'var(--text-primary)' }}>
                                  {issue.summary}
                                </div>
                                {issue.clientEmr && (
                                  <div
                                    className="mt-0.5 truncate text-[9px] rounded px-1"
                                    style={{ background: 'rgba(6,182,212,0.15)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.2)' }}
                                  >
                                    {issue.clientEmr}
                                  </div>
                                )}
                              </div>
                            ))}
                            {overflow > 0 && (
                              <div className="text-[9px] font-semibold text-center" style={{ color: 'var(--accent-blue)' }}>
                                +{overflow} more
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: 'var(--text-dimmed)' }}>
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded" style={{ background: 'rgba(13,207,184,0.12)', border: '1px solid rgba(13,207,184,0.35)' }} /> go-live scheduled
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded" style={{ background: 'rgba(255,209,102,0.08)', border: '1px solid rgba(255,209,102,0.35)' }} /> today
                  </span>
                </div>
              </>
            )}

            {/* Year view */}
            {view === 'year' && (
              <div className="grid grid-cols-4 gap-3 flex-1 overflow-y-auto">
                {Array.from({ length: 12 }, (_, m) => (
                  <MiniMonth
                    key={m}
                    year={year}
                    month={m}
                    issues={issues}
                    onClick={() => { setYear(year); setMonth(m); setView('month'); scrollAgendaToMonth(year, m); }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Agenda panel */}
        {agendaOpen && (
          <div
            className="w-72 shrink-0 rounded-2xl overflow-hidden flex flex-col"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Agenda</span>
              <button
                type="button"
                onClick={() => setAgendaOpen(false)}
                className="text-xs rounded-lg px-2 py-0.5"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dimmed)', border: '1px solid var(--border)' }}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {isLoading && (
                <div className="text-xs text-center py-4" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>
              )}
              {!isLoading && issues.length === 0 && !error && (
                <div className="text-xs text-center py-4" style={{ color: 'var(--text-dimmed)' }}>No go-live dates found.</div>
              )}
              {[...byMonth.entries()].map(([key, monthIssues]) => {
                const parts = key.split('-').map(Number);
                const y = parts[0] ?? 0;
                const m = parts[1] ?? 1;
                return (
                  <div key={key} ref={(el) => { agendaRefs.current[key] = el; }}>
                    <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--accent-blue)' }}>
                      {MONTH_NAMES[m - 1]} {y}
                    </div>
                    <div className="space-y-1">
                      {monthIssues.map((issue) => {
                        const d = parseGoLiveDate(issue.goLiveDate!) ?? new Date();
                        return (
                          <button
                            key={issue.key}
                            type="button"
                            onClick={() => void navigate({ to: '/tickets/$key', params: { key: issue.key } })}
                            className="w-full text-left flex items-start gap-2 rounded-xl px-2 py-2 transition-colors"
                            style={{ border: '1px solid var(--border)' }}
                            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent-blue)')}
                            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                          >
                            <div className="min-w-[32px] text-center">
                              <div className="text-base font-bold leading-none" style={{ color: statusColor(issue.status) }}>
                                {String(d.getDate()).padStart(2, '0')}
                              </div>
                              <div className="text-[9px]" style={{ color: 'var(--text-dimmed)' }}>
                                {DAY_NAMES[d.getDay()]}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-[10px]" style={{ color: 'var(--text-dimmed)' }}>{issue.key}</div>
                              <div className="text-xs truncate font-medium" style={{ color: 'var(--text-primary)' }}>{issue.summary}</div>
                              <div className="flex items-center gap-1 mt-0.5">
                                {issue.clientEmr && (
                                  <span className="rounded px-1 py-0.5 text-[9px]" style={{ background: '#06b6d411', color: '#06b6d4', border: '1px solid #06b6d422' }}>
                                    {issue.clientEmr}
                                  </span>
                                )}
                                <span
                                  className="rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                                  style={{ background: statusBg(issue.status), color: statusColor(issue.status), border: `1px solid ${statusBorder(issue.status)}` }}
                                >
                                  {issue.status}
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
